import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  IconAlertCircle,
  IconBriefcase,
  IconClock,
  IconLock,
  IconMail,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip } from '@/components/ui/tooltip';
import { relativeTime } from '@/lib/utils';
import { api, type UserDetail } from '../api.js';

export function UserDetailPage() {
  const { user } = useParams<{ user: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['users.detail', user],
    queryFn: () => api.userDetail(user ?? ''),
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center gap-2">
          <Spinner />
          <span className="text-sm text-muted-foreground">Loading profile…</span>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-5xl">
        <Alert tone="destructive">
          <IconAlertCircle />
          <AlertTitle>Failed to load user</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4">
      <ProfileHeader detail={data} />
      <ActivityGrid activity={data.activity} />
      <RecentChanges detail={data} />
      <OpenedFiles detail={data} />
      <Workspaces detail={data} />
    </div>
  );
}

function ProfileHeader({ detail }: { detail: UserDetail }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1.5 py-4">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h1 className="font-mono text-2xl font-semibold tracking-tight">
            {detail.user}
          </h1>
          {detail.fullName && (
            <span className="text-base text-muted-foreground">
              {detail.fullName}
            </span>
          )}
          {detail.type && <Badge>{detail.type}</Badge>}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {detail.email && (
            <span className="inline-flex items-center gap-1 text-xs">
              <IconMail size={13} className="text-muted-foreground" />
              <a
                href={`mailto:${detail.email}`}
                className="text-theia-400 hover:underline"
              >
                {detail.email}
              </a>
            </span>
          )}
          {detail.accessAt && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <IconClock size={13} />
              last access {relativeTime(detail.accessAt)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Activity grid: GitHub-style 53-week × 7-day heatmap, columns aligned to ISO
// weeks. Each cell is a Sunday-anchored day. Color buckets approximate
// GitHub's contribution graph (0, 1, 2, 3, 4+ tiers).
// ---------------------------------------------------------------------------

interface ActivityCell {
  date: string;
  count: number;
}

function ActivityGrid({ activity }: { activity: ActivityCell[] }) {
  const grid = useMemo(() => buildGrid(activity), [activity]);
  const total = activity.reduce((sum, a) => sum + a.count, 0);

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Activity</h2>
          <span className="text-xs text-muted-foreground">
            {total} submit{total === 1 ? '' : 's'} in the last year
          </span>
        </div>
        <div className="mt-3 overflow-x-auto">
          <div
            className="grid"
            style={{
              gridTemplateRows: 'repeat(7, 12px)',
              gridAutoColumns: '12px',
              gridAutoFlow: 'column',
              gap: 2,
              padding: '4px 0',
              minWidth: 53 * 14,
            }}
          >
            {grid.map((cell, idx) =>
              cell ? (
                <Tooltip
                  key={idx}
                  label={`${cell.count} submit${cell.count === 1 ? '' : 's'} on ${cell.date}`}
                >
                  <div
                    className="rounded-sm"
                    style={{
                      width: 12,
                      height: 12,
                      background: colorForCount(cell.count),
                    }}
                  />
                </Tooltip>
              ) : (
                <div key={idx} style={{ width: 12, height: 12 }} />
              ),
            )}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span>Less</span>
          {[0, 1, 3, 6, 10].map((n) => (
            <div
              key={n}
              className="rounded-sm"
              style={{ width: 12, height: 12, background: colorForCount(n) }}
            />
          ))}
          <span>More</span>
        </div>
      </CardContent>
    </Card>
  );
}

function buildGrid(activity: ActivityCell[]): Array<ActivityCell | null> {
  // Build a sparse map for O(1) lookup.
  const map = new Map<string, number>();
  for (const a of activity) map.set(a.date, a.count);

  // Anchor: start on the Sunday at-or-before "today minus 52 weeks", so
  // the rightmost column is the current week and the grid contains
  // exactly 53 weeks × 7 days.
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const end = new Date(today);
  // Roll forward to the upcoming Saturday so the last column has all 7
  // days laid out (even if some are future-dated and stay empty).
  end.setUTCDate(end.getUTCDate() + (6 - end.getUTCDay()));

  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (53 * 7 - 1));

  const cells: Array<ActivityCell | null> = [];
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    const iso = cursor.toISOString().slice(0, 10);
    const future = cursor.getTime() > Date.now();
    cells.push(future ? null : { date: iso, count: map.get(iso) ?? 0 });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return cells;
}

function colorForCount(n: number): string {
  // GitHub's dark-mode contribution palette, swapped to lean
  // toward the Theia-orange family — keeps the visual cohesion
  // with the rest of the app instead of a free-floating green.
  if (n === 0) return '#161616';
  if (n < 3) return '#3a1d0a';
  if (n < 6) return '#6b2f10';
  if (n < 10) return '#a64217';
  return '#e55b2b';
}

// ---------------------------------------------------------------------------
// Recent changes / opened files / workspaces — three plain sections.
// ---------------------------------------------------------------------------

function RecentChanges({ detail }: { detail: UserDetail }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 py-4">
        <h2 className="text-sm font-semibold">Recent changes</h2>
        {detail.recentChanges.length === 0 ? (
          <p className="text-xs text-muted-foreground">No recent submits.</p>
        ) : (
          <div className="flex flex-col">
            {detail.recentChanges.map((c, i) => (
              <div
                key={c.change}
                className={i === 0 ? 'py-2' : 'border-t border-neutral-800/60 py-2'}
              >
                <div className="flex items-baseline gap-2">
                  <Link
                    to={`/changes/${c.change}`}
                    className="text-xs text-theia-400 hover:underline"
                  >
                    @{c.change}
                  </Link>
                  {c.client && (
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {c.client}
                    </span>
                  )}
                  <span className="text-[11px] text-muted-foreground">
                    {c.time ? new Date(c.time).toLocaleString() : '—'}
                  </span>
                </div>
                {c.description && (
                  <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-muted-foreground">
                    {firstLine(c.description)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OpenedFiles({ detail }: { detail: UserDetail }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 py-4">
        <div className="flex items-baseline justify-between">
          <h2 className="inline-flex items-center gap-1.5 text-sm font-semibold">
            <IconLock size={14} className="text-muted-foreground" />
            Currently open
          </h2>
          <span className="text-xs text-muted-foreground">
            {detail.openedFiles.length} file
            {detail.openedFiles.length === 1 ? '' : 's'}
          </span>
        </div>
        {detail.openedFiles.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No files currently checked out.
          </p>
        ) : (
          <div className="overflow-hidden rounded-md border border-neutral-800">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Workspace</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Change</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.openedFiles.map((f) => (
                  <TableRow key={`${f.depotFile}@${f.rev}@${f.client}`}>
                    <TableCell className="font-mono">
                      <Link
                        to={`/depot${f.depotFile.replace(/^\/\//, '/')}`}
                        className="text-theia-400 hover:underline"
                      >
                        {f.depotFile}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {f.client ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{f.action ?? '?'}</Badge>
                    </TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">
                      {f.change ?? 'default'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Workspaces({ detail }: { detail: UserDetail }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 py-4">
        <div className="flex items-baseline justify-between">
          <h2 className="inline-flex items-center gap-1.5 text-sm font-semibold">
            <IconBriefcase size={14} className="text-muted-foreground" />
            Workspaces
          </h2>
          <span className="text-xs text-muted-foreground">
            {detail.workspaces.length}
          </span>
        </div>
        {detail.workspaces.length === 0 ? (
          <p className="text-xs text-muted-foreground">No workspaces.</p>
        ) : (
          <div className="overflow-hidden rounded-md border border-neutral-800">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>Last access</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.workspaces.map((w) => (
                  <TableRow key={w.client}>
                    <TableCell className="font-mono">{w.client}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {w.host ?? '—'}
                    </TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">
                      {relativeTime(w.accessAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function firstLine(s: string): string {
  const i = s.indexOf('\n');
  return i >= 0 ? s.slice(0, i) : s;
}

